import moment from 'moment';
import React from 'react';
import { nicelyFormatDate } from '../../../../generic/common';
import styles from './Style/Notifications.module.scss';
import MikeNotificationItemGroup from './MikeNotificationItemGroup';
import { INotification } from './INotification';
import { INotificationPerDate, INotifications } from './INotifications';

interface IProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Notifications.
   */
  notifications: Array<INotification>;
  /**
   * href.
   */
  href?: string;
  /**
   * Link callback.
   */
  onLinkCallBack?: (arg0: INotification) => void;
  /**
   * Custom title.
   */
  title?: string;
  /**
   * Custom 'no content' label.
   */
  noContentLabel?: string;
  /**
   * Custom render function.
   * To display the content of a notification item.
   */
  renderContent?: (notification: INotification) => React.ReactNode | string;
  /**
   * Custom render function.
   * To display the title of a notification item.
   */
  renderTitle?: (notification: INotification) => React.ReactNode | string;
}

// maxWidth: '400px', maxHeight: '400px'

/**
 * @name MikeNotificationGroups
 * @summary A component for grouping notification items per day.
 */
export const MikeNotificationGroups: React.FC<IProps> = ({
  notifications,
  href,
  onLinkCallBack,
  title = 'Notifications',
  noContentLabel = 'No notifications to show yet',
  renderContent,
  renderTitle,
}: IProps) => {
  const sortedNotifications: Array<INotification> =
    notifications &&
    notifications.sort((a: INotification, b: INotification) => {
      return b.time - a.time;
    });

  // this gives an object with dates as keys
  const dailyNotifications: INotificationPerDate =
    sortedNotifications &&
    sortedNotifications.reduce((notificationsByDate: any, notification) => {
      const date = nicelyFormatDate(moment(notification.time));
      if (!notificationsByDate[date]) {
        notificationsByDate[date] = new Array<INotification>();
      }
      notificationsByDate[date].push(notification);
      return notificationsByDate;
    }, {});

  const notificationPerDay: Array<INotifications> =
    dailyNotifications &&
    Object.keys(dailyNotifications).map((date: string) => {
      return {
        date,
        notifications: dailyNotifications[date],
      };
    });

  return (
    <>
      <div className={styles.notificationsTitle}>{title}</div>
      {!notificationPerDay || notificationPerDay.length === 0 ? (
        <div className={styles.noContent}>{noContentLabel}</div>
      ) : (
        notificationPerDay.map(
          (notificationsOfTheDay: INotifications, index: number) => (
            <MikeNotificationItemGroup
              key={index}
              notificationsPerDate={notificationsOfTheDay}
              href={href}
              isFirst={index === 0}
              onLinkCallBack={onLinkCallBack}
              renderContent={renderContent}
              renderTitle={renderTitle}
            />
          )
        )
      )}
    </>
  );
};

export default MikeNotificationGroups;
